import React from "react";
import { View, Text, StyleSheet, FlatList, Platform, useWindowDimensions } from "react-native";
import { useLikes } from "../hooks/useLikes";
import { ProductCard } from "../components/products/ProductCard";
import { ProductCardSkeleton } from "../components/ui/ProductCardSkeleton";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../types/navigation";

// Match the phone container max width from App.tsx
const PHONE_CONTAINER_MAX_WIDTH = 430;

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function LikesScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NavigationProp>();
  const { likes, isLoading } = useLikes();
  
  // Use useWindowDimensions for responsive sizing
  const windowDimensions = useWindowDimensions();
  const effectiveWidth = Platform.OS === 'web'
    ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
    : windowDimensions.width;

  const cardWidth = (effectiveWidth - spacing.lg * 3) / 2;

  const renderSkeletonData = () =>
    Array.from({ length: 6 }).map((_, index) => ({
      id: `skeleton-${index}`,
      isSkeleton: true,
    }));

  const renderProductItem = ({ item }: { item: any }) => {
    if (item.isSkeleton) {
      return (
        <View style={styles.productWrapper}>
          <ProductCardSkeleton cardWidth={cardWidth} />
        </View>
      );
    }

    return (
      <View style={styles.productWrapper}>
        <ProductCard
          product={item.products}
          cardWidth={cardWidth}
          onPress={() =>
            navigation.navigate("ProductDetails", {
              id: item.products.id,
            })
          }
        />
      </View>
    );
  };

  const renderEmptyComponent = () => (
    <View style={styles.emptyState}>
      <Feather name="heart" size={64} color={colors.textLight} />
      <Text style={styles.emptyText}>No liked products yet</Text>
      <Text style={styles.emptySubtext}>
        Products you like will appear here
      </Text>
    </View>
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.title}>Liked Products</Text>
    </View>
  );

  const data = isLoading ? renderSkeletonData() : likes;

  return (
    <FlatList
      style={styles.container}
      data={data}
      renderItem={renderProductItem}
      keyExtractor={(item) => (item.isSkeleton ? item.id : item.id)}
      numColumns={2}
      columnWrapperStyle={styles.row}
      ListHeaderComponent={renderHeader}
      ListEmptyComponent={!isLoading ? renderEmptyComponent : null}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    />
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: {
    ...typography.title,
    color: colors.text,
    fontSize: 28,
    fontWeight: "700",
  },
  content: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  row: {
    justifyContent: "space-between",
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
  },
  productWrapper: {
    width: "48%",
    marginBottom: spacing.md,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xl * 3,
    paddingHorizontal: spacing.lg,
  },
  emptyText: {
    ...typography.title,
    color: colors.textLight,
    marginTop: spacing.lg,
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
  },
  emptySubtext: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.sm,
    textAlign: "center",
    lineHeight: 20,
  },
});
